/**
 * Collapsed Sidebar — a 40px activity rail (CH-15)
 *
 * Shows a compact sidebar with current connection/database context when collapsed.
 * Displays:
 * - Connection engine icon (with a tooltip carrying the connection name) -
 *   clickable dropdown to change connection, with a blue marker when active
 * - Current database context (icon + tooltip) - clickable dropdown to change database
 * - Connect button if no active connection
 * - Reconnect button if disconnected
 *
 * No vertical/rotated text: the collapsed rail carries information via
 * tooltips (hover) and `aria-label`s (accessible name), never
 * `writing-mode`/`rotate` CSS — see `Docs/UiNativeWave-Plan.md` CH-15.
 */

import { useState, useMemo } from 'react';
import { PanelLeftOpen, PlugZap, AlertTriangle, RefreshCw, Database, Check, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { useSidebarStore } from '../../features/database-objects/store/sidebarStore';
import { useConnectionStore } from '../../features/connections/store/connectionStore';
import { useDatabaseStore } from '../../features/database-objects/store/databaseStore';
import { useDatabaseUIStore } from '../../features/database-objects/store/uiStore';
import { useConnectionsUIStore } from '../../features/connections/store/uiStore';
import { ConnectionEngineIcon, getEngineLabel } from '../../features/connections/components/ConnectionEngineIcon';
import { CollapsedSidebarRailButton } from './CollapsedSidebarRailButton';
import { CollapsedSidebarDatabaseMenu } from './CollapsedSidebarDatabaseMenu';
import { supportsQueryEditorEngineFilter } from '../../features/main-toolbar/model/toolbarEngineFilters';
import { cn } from '../../lib/utils';
import { useShortcutLabel } from '../../features/keybindings/hooks/useShortcutLabel';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { safeConnectionName } from '@/types/safeConnectionDisplay';
export function CollapsedSidebar() {
  const toggleSidebarShortcutLabel = useShortcutLabel('global.toggleSidebar');
  const expand = useSidebarStore((s) => s.expand);
  // Connection state
  const connections = useConnectionStore((s) => s.connections);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const connectToDatabase = useConnectionStore((s) => s.connectToDatabase);

  // Database tree state
  const treeData = useDatabaseStore((s) => s.treeData);

  // UI state
  const selectedConnectionId = useDatabaseUIStore((s) => s.selectedConnectionId);
  const selectedDatabase = useDatabaseUIStore((s) => s.selectedDatabase);
  const setSelectedConnectionId = useDatabaseUIStore((s) => s.setSelectedConnectionId);
  const setSelectedDatabase = useDatabaseUIStore((s) => s.setSelectedDatabase);
  const setShowConnectionsDialog = useConnectionsUIStore(
    (s) => s.setShowConnectionsDialog
  );

  // Local reconnecting state
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Find the currently selected connection
  const selectedConnection = activeConnections.find(
    (c) => c.id === selectedConnectionId
  );
  const isDisconnected = selectedConnection?.status === 'disconnected';

  // Check if we have any active connections at all
  const hasAnyConnection = activeConnections.length > 0;

  // Rail tooltip/aria-label text for the selected connection (CH-15) — the
  // information the old vertical label used to carry, now on hover + a11y
  // name instead of rotated text.
  const connectionDisplayName = safeConnectionName(selectedConnection?.connection, '');
  const connectionEngineLabel = getEngineLabel(selectedConnection?.connection.engine);

  // Get connections from activeConnections (always available, even when DatabaseExplorer hasn't mounted)
  // Falls back to treeData for display name if available, otherwise uses connection alias/server
  const treeConnections = useMemo(() => {
    return activeConnections.map((activeConn) => {
      // Try to get the display name from treeData if available
      const treeNode = treeData.find(
        (node) => node.type === 'connection' && node.metadata?.id === activeConn.id
      );
      const displayName = treeNode?.name ?? safeConnectionName(activeConn.connection);

      return {
        id: activeConn.id,
        name: displayName,
        engine: activeConn.connection.engine,
        status: activeConn.status,
      };
    });
  }, [activeConnections, treeData]);

  // Handle connect button click
  const handleConnect = () => {
    setShowConnectionsDialog(true);
  };

  // Handle connection selection from dropdown
  const handleSelectConnection = (connectionId: string) => {
    setSelectedConnectionId(connectionId);
    // Clear database selection when changing connection
    setSelectedDatabase(undefined);
  };

  // Handle database selection from dropdown
  const handleSelectDatabase = (database: string) => {
    // V5 — stamp the owning connection so the store coerces this selection
    // against the CURRENT connection's limit, not a stale pairing.
    setSelectedDatabase(database, selectedConnectionId);
  };

  // Handle reconnect button click
  const handleReconnect = async () => {
    if (!selectedConnectionId || isReconnecting) return;

    // Find the saved connection config
    const connection = connections.find((c) => c.id === selectedConnectionId);
    if (!connection) return;

    setIsReconnecting(true);
    try {
      await connectToDatabase(connection);
    } finally {
      setIsReconnecting(false);
    }
  };

  return (
    /* CH-10: same surface + seam as the expanded sidebar, so collapsing
       does not change which colour the explorer column is painted. */
    <div
      className="h-full w-10 flex flex-col items-center py-2 bg-sidebar border-r border-divider"
      data-testid="collapsed-sidebar"
    >
      {/* Expand button at top */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 mb-1"
        title={`Expand Object Explorer${toggleSidebarShortcutLabel ? ` (${toggleSidebarShortcutLabel})` : ''}`}
        onClick={expand}
      >
        <PanelLeftOpen className="h-4 w-4" />
        <span className="sr-only">Expand Object Explorer</span>
      </Button>

      {/* Connection/Database context */}
      <div className="flex-1 flex flex-col items-center gap-2 overflow-hidden">
        {!hasAnyConnection ? (
          /* No active connections - show connect button */
          <Tooltip>
            <TooltipTrigger asChild>
              <CollapsedSidebarRailButton
                icon={<PlugZap className="h-4 w-4" />}
                aria-label="Connect to a database"
                onClick={handleConnect}
                data-testid="rail-connect-button"
              />
            </TooltipTrigger>
            <TooltipContent side="right">Connect to a database</TooltipContent>
          </Tooltip>
        ) : selectedConnection ? (
          /* Has selected connection - show connection info with dropdown */
          <div className="flex flex-col items-center gap-2 py-1">
            {/* Connection dropdown — engine icon + blue "active" marker when
                connected, tooltip carries the name the old vertical label did */}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <CollapsedSidebarRailButton
                      icon={
                        <ConnectionEngineIcon
                          engine={selectedConnection.connection.engine}
                          size="sm"
                          className={cn(isDisconnected && 'opacity-50')}
                        />
                      }
                      isActive={!isDisconnected}
                      className={cn(isDisconnected && 'opacity-70')}
                      aria-label={`Connection: ${connectionDisplayName} (${connectionEngineLabel})${isDisconnected ? ', disconnected' : ''}. Click to change connection.`}
                      data-testid="rail-connection-button"
                    />
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {connectionDisplayName}
                  <span className="text-primary-foreground/75"> · {connectionEngineLabel}</span>
                  {isDisconnected && <span className="text-warning"> · Disconnected</span>}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent side="right" align="start" className="w-48">
                <DropdownMenuLabel className="text-xs">Connections</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {treeConnections.map((conn) => (
                  <DropdownMenuItem
                    key={conn.id}
                    onClick={() => handleSelectConnection(conn.id!)}
                    className="flex items-center gap-2"
                  >
                    <ConnectionEngineIcon engine={conn.engine} size="sm" />
                    <span className="flex-1 truncate">{conn.name}</span>
                    {conn.id === selectedConnectionId && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleConnect}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>New connection</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Disconnected indicator and reconnect button */}
            {isDisconnected && (
              <div className="flex flex-col items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
                <button
                  className="p-1 rounded hover:bg-amber-200 dark:hover:bg-amber-800 transition-colors"
                  title="Reconnect"
                  aria-label="Reconnect"
                  onClick={handleReconnect}
                  disabled={isReconnecting}
                  data-testid="rail-reconnect-button"
                >
                  {isReconnecting ? (
                    <RandomDotmSpinner size={12} className="text-warning" />
                  ) : (
                    <RefreshCw className="h-3 w-3 text-warning" />
                  )}
                </button>
              </div>
            )}

            {/* Database dropdown (if connected). An engine with no query
                language (Azure Storage) has no database list: the shared
                listing could only report its refusal. */}
            {!isDisconnected && supportsQueryEditorEngineFilter(selectedConnection?.connection.engine) && (
              <CollapsedSidebarDatabaseMenu
                connectionId={selectedConnectionId}
                selectedDatabase={selectedDatabase}
                onSelectDatabase={handleSelectDatabase}
              />
            )}
          </div>
        ) : (
          /* Has connections but none selected - show dropdown to select */
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <CollapsedSidebarRailButton
                    icon={<Database className="h-4 w-4 opacity-50" />}
                    className="text-muted-foreground"
                    aria-label="Select a connection"
                    data-testid="rail-select-connection-button"
                  />
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">Select a connection</TooltipContent>
            </Tooltip>
            <DropdownMenuContent side="right" align="start" className="w-48">
              <DropdownMenuLabel className="text-xs">Connections</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {treeConnections.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  No connections in explorer
                </div>
              ) : (
                treeConnections.map((conn) => (
                  <DropdownMenuItem
                    key={conn.id}
                    onClick={() => handleSelectConnection(conn.id!)}
                    className="flex items-center gap-2"
                  >
                    <ConnectionEngineIcon engine={conn.engine} size="sm" />
                    <span className="flex-1 truncate">{conn.name}</span>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleConnect}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                <span>New connection</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
